import React from 'react';
import WaveSurfer from 'wavesurfer.js';
import MarkersPlugin from "wavesurfer.js/dist/plugin/wavesurfer.markers";
import RegionsPlugin from "wavesurfer.js/dist/plugin/wavesurfer.regions";
import { showAlert } from "../common/functions";
import { renewToken, getRenewalInProgress, setRenewalInProgress, getRenewalAllowedTime } from "../../pages/common/functions";
import fakePeaks from '../common/peaks.json';

const MAX_REAL_PEAKS_DURATION = 7200;
interface IHeaderProps {
	src: any;
	onPositionChange?: (position: number) => void;
	onReady?: () => void;
	onDurationChange?: (duration: number) => void;
	isPlay: boolean;
	isAvoidError?: boolean;
	id: string;
	callback: (isPlaying: boolean) => void;
}

interface IWaveformState {
	playing: boolean;
	loading: boolean;
	error: string | null;
	usingFakePeaks: boolean;
	bufferProgress: number;
}

export default class Waveform extends React.Component<IHeaderProps, IWaveformState> {
	private wavesurfer: WaveSurfer | null = null;
	private waveformRef = React.createRef<HTMLDivElement>();
	private isUnmounted = false;
	private audioElement: HTMLAudioElement | null = null;
	private objectUrl: string | null = null;
	private isSyncing = false;
	private lastPosition = 0;

	constructor(props: IHeaderProps) {
		super(props);
		this.state = {
			playing: false,
			loading: false,
			error: null,
			usingFakePeaks: false,
			bufferProgress: 0,
		};
	}

	componentDidMount() {
		this.initializeWaveSurfer();
	}

	componentDidUpdate(prevProps: IHeaderProps) {
		if (prevProps.isPlay !== this.props.isPlay &&
			this.props.isPlay !== this.state.playing) {
			this.handlePlay();
		}

		if (prevProps.src !== this.props.src) {
			this.destroyWaveSurfer();
			this.initializeWaveSurfer();
		}
	}

	componentWillUnmount() {
		this.isUnmounted = true;
		this.destroyWaveSurfer();
	}

	private handlePlay = () => {
		const shouldPlay = !this.state.playing;
		this.setState({ playing: shouldPlay }, () => {
			if (shouldPlay) {
				this.playAudio();
			} else {
				this.pauseAudio();
			}
			this.props.callback(shouldPlay);
		});
	};

	private playAudio = () => {
		if (this.state.usingFakePeaks && this.audioElement) {
			this.audioElement.play().catch(e => { });
		} else if (this.wavesurfer) {
			this.wavesurfer.play();
		}
	};

	private pauseAudio = () => {
		if (this.state.usingFakePeaks && this.audioElement) {
			this.audioElement.pause();
		} else if (this.wavesurfer) {
			this.wavesurfer.pause();
		}
	};

	private handleWaveformSeek = (progress: number) => {
		if (this.isSyncing || !this.audioElement) return;

		this.isSyncing = true;
		const duration = this.audioElement.duration || 1;
		const newTime = Math.max(0, Math.min(progress * duration, duration));
		this.lastPosition = newTime;

		this.audioElement.ontimeupdate = null;
		this.audioElement.currentTime = newTime;

		if (this.wavesurfer) {
			this.wavesurfer.seekTo(newTime / duration);
		}
		this.props.onPositionChange?.(newTime);

		setTimeout(() => {
			if (this.audioElement) {
				this.audioElement.ontimeupdate = this.handleAudioUpdate.bind(this);
			}
			this.isSyncing = false;
		}, 100);
	};

	private handleAudioUpdate = () => {
		if (this.isSyncing || !this.wavesurfer || !this.audioElement) return;

		const duration = this.audioElement.duration || 1;
		const currentTime = this.audioElement.currentTime;

		this.sessionHandler();

		if (Math.abs(currentTime - this.lastPosition) > 0.02) {
			this.isSyncing = true;
			this.lastPosition = currentTime;

			this.wavesurfer.seekTo(currentTime / duration);
			this.props.onPositionChange?.(currentTime);

			setTimeout(() => {
				this.isSyncing = false;
			}, 30);
		}
	};

	private setupFakePeaksSync = () => {
		if (!this.wavesurfer || !this.audioElement) return;

		this.audioElement.ontimeupdate = null;
		this.wavesurfer.un('seek');

		this.audioElement.ontimeupdate = this.handleAudioUpdate.bind(this);
		this.wavesurfer.on('seek', this.handleWaveformSeek.bind(this));

		if (this.audioElement.currentTime > 0) {
			this.handleWaveformSeek(this.audioElement.currentTime / (this.audioElement.duration || 1));
		}
	};

	private async fetchAndStreamAudio(url: string) {
		const response = await fetch(url);
		if (!response.body) throw new Error("ReadableStream not supported or empty response");

		const reader = response.body.getReader();
		const chunks: Uint8Array[] = [];

		let receivedLength = 0;
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			if (value) {
				chunks.push(value);
				receivedLength += value.length;
				const contentLength = response.headers.get('Content-Length');
				if (contentLength) {
					const percent = (receivedLength / parseInt(contentLength)) * 100;
					this.setState({ bufferProgress: percent });
				}
				this.sessionHandler();
			}
		}
		const audioBlob = new Blob(chunks, { type: 'audio/mpeg' });
		return URL.createObjectURL(audioBlob);
	}

	private setupFakePeaksWithAudio = async () => {
		try {
			this.destroyWaveSurfer();
			this.setState({ usingFakePeaks: true, loading: true });

			this.audioElement = new Audio();
			this.audioElement.preload = 'metadata';

			this.audioElement.addEventListener('progress', () => {
				if (!this.audioElement || !this.audioElement.duration) return;
				const buffered = this.audioElement.buffered;
				let bufferedEnd = 0;
				if (buffered.length) {
					bufferedEnd = buffered.end(buffered.length - 1);
				}
				const percent = (bufferedEnd / this.audioElement.duration) * 100;
				this.setState({ bufferProgress: percent });
			})

			if (typeof this.props.src === 'string') {
				this.objectUrl = await this.fetchAndStreamAudio(this.props.src);
				this.audioElement.src = this.objectUrl
			} else {
				this.objectUrl = URL.createObjectURL(this.props.src);
				this.audioElement.src = this.objectUrl;
			}

			await new Promise<void>((resolve, reject) => {
				this.audioElement!.oncanplaythrough = () => resolve();
				this.audioElement!.onerror = () => reject(new Error('Audio failed to load'));
			});

			if (this.audioElement && this.audioElement.duration) {
				this.props.onDurationChange?.(this.audioElement.duration);
			}

			this.wavesurfer = WaveSurfer.create({
				container: this.waveformRef.current!,
				backend: this.props.id === "cors" ? "WebAudio" : "MediaElement",
				waveColor: 'red',
				progressColor: 'purple',
				responsive: true,
				fillParent: true,
				interact: true,
				duration: this.audioElement.duration,
				plugins: [
					MarkersPlugin.create({ time: 2, label: "V2", color: '#00ffcc', position: 'top' }),
					RegionsPlugin.create({
						id: "region-2", start: 5, end: 25, color: "rgba(225, 195, 100, .5)", data: { systemRegionId: 32 }
					})
				],
				barWidth: 0.5,
				barGap: 0.5,
				barRadius: 1,
			});
			setTimeout(async () => {
				if (this.isUnmounted) return;
				const emptyAudio = new Blob([new Uint8Array(0)], { type: 'audio/wav' });
				const emptyUrl = URL.createObjectURL(emptyAudio);

				await this.wavesurfer.load(emptyUrl, fakePeaks.data);
				this.wavesurfer.setVolume(0);

				this.setupFakePeaksSync();

				this.setState({ loading: false });
				this.props.onReady?.();
			}, 2500);

		} catch (error) {
			this.handleError(error);
		}
	};

	private setupRealWaveform = async () => {
		try {
			this.destroyWaveSurfer();
			this.setState({ usingFakePeaks: false, loading: true });

			this.wavesurfer = WaveSurfer.create({
				container: this.waveformRef.current!,
				backend: this.props.id === "cors" ? "WebAudio" : "MediaElement",
				xhr: { mode: this.props.id },
				waveColor: 'red',
				progressColor: 'purple',
				responsive: true,
				fillParent: true,
				partialRender: true,
				pixelRatio: window.devicePixelRatio || 1,
				minPxPerSec: 10,
				hideScrollbar: true,
				splitChannels: false,
				scrollParent: false,
				barWidth: 0.5,
				barGap: 0.5,
				barRadius: 1,
				plugins: [
					MarkersPlugin.create({ time: 2, label: "V2", color: '#00ffcc', position: 'top' }),
					RegionsPlugin.create({
						id: "region-2", start: 5, end: 25, color: "rgba(225, 195, 100, .5)", data: { systemRegionId: 32 }
					})
				]
			});

			this.wavesurfer.on("audioprocess", (pos: number) => {
				this.props.onPositionChange?.(pos);
				this.sessionHandler();
			});

			this.wavesurfer.on("seek", (progress: number) => {
				if (this.wavesurfer) {
					this.props.onPositionChange?.(progress * this.wavesurfer.getDuration());
				}
			});

			if (typeof this.props.src === "string") {
				this.props.src === 'cors' ?
					this.wavesurfer.load(this.props.src) :
					this.wavesurfer.load(this.props.src, fakePeaks.data)
			} else {
				this.objectUrl = URL.createObjectURL(this.props.src);
				await this.wavesurfer.load(this.objectUrl);
			}

			this.wavesurfer.on("ready", () => {
				if (!this.isUnmounted) {
					this.setState({ loading: false });
					if (this.wavesurfer) {
						this.props.onDurationChange?.(this.wavesurfer.getDuration());
					}
					this.props.onReady?.();
				}
			});

			this.wavesurfer.on("finish", () => {
				if (this.state.playing) {
					this.setState({ playing: false });
					this.props.callback(false);
				}
			});

		} catch (error) {
			await this.setupFakePeaksWithAudio();
		}
	};

	private destroyWaveSurfer = () => {
		this.isSyncing = false;

		if (this.wavesurfer) {
			this.wavesurfer.unAll();
			this.wavesurfer.destroy();
			this.wavesurfer = null;
		}

		if (this.audioElement) {
			this.audioElement.ontimeupdate = null;
			this.audioElement.pause();
			this.audioElement.src = '';
			this.audioElement = null;
		}

		if (this.objectUrl) {
			URL.revokeObjectURL(this.objectUrl);
			this.objectUrl = null;
		}
	};

	private initializeWaveSurfer = async () => {
		try {
			this.setState({ loading: true, error: null });

			if (!this.waveformRef.current) {
				throw new Error('Waveform container not found');
			}

			const duration = await this.getAudioDuration(this.props.src);
			const formatSupported = await this.checkAudioFormat(this.props.src);

			if (duration > MAX_REAL_PEAKS_DURATION || !formatSupported || this.props.id === 'cors') {
				await this.setupFakePeaksWithAudio();
			} else {
				await this.setupRealWaveform();
			}
		} catch (error) {
			this.handleError(error);
		}
	};

	private getAudioDuration = (src: any): Promise<number> => {
		return new Promise((resolve) => {
			const audio = new Audio();
			if (typeof src === 'string') {
				audio.src = src;
			} else {
				const url = URL.createObjectURL(src);
				audio.src = url;
				audio.onloadedmetadata = () => URL.revokeObjectURL(url);
			}
			audio.onloadedmetadata = () => resolve(audio.duration);
			audio.onerror = () => resolve(0);
		});
	};

	private checkAudioFormat = async (src: any): Promise<boolean> => {
		try {
			const audio = new Audio();
			if (typeof src === 'string') {
				audio.src = src;
			} else {
				const url = URL.createObjectURL(src);
				audio.src = url;
				audio.onloadedmetadata = () => URL.revokeObjectURL(url);
			}
			await new Promise((resolve, reject) => {
				audio.oncanplay = resolve;
				audio.onerror = reject;
			});
			return true;
		} catch {
			return false;
		}
	};

	private handleError = (error: unknown) => {
		if (this.isUnmounted) return;

		const errorMessage = error instanceof Error
			? error.message
			: typeof error === 'string'
				? error
				: 'Unknown error occurred';

		this.setState({
			loading: false,
			playing: false,
			error: errorMessage
		});

		if (!this.state.usingFakePeaks && !this.props.isAvoidError) {
			showAlert(`Failed to load audio: ${errorMessage}`, true);
		}

		this.props.onReady?.();
	};

	private sessionHandler = async () => {
		const sessionExpiry = window.localStorage.getItem('SESSIONEXPIRY');
		let isRenewalInProgress: boolean = getRenewalInProgress();

		if (Date.now() > Number(sessionExpiry)) {
			if (!isRenewalInProgress) {
				if (Date.now() < (Number(sessionExpiry) + getRenewalAllowedTime())) {
					isRenewalInProgress = true;
					setRenewalInProgress(isRenewalInProgress);
					const isSuccess = await renewToken();
					if (isSuccess) {
						isRenewalInProgress = false;
						setRenewalInProgress(isRenewalInProgress);
					}
				}
			}
		}
	};

	render() {
		return (
			<div>
				<div className='waveform' data-testid="waveFormContainer">
					<div ref={this.waveformRef} data-testid="waveContainer" id={'wave' + this.props.id} className='wave' />
				</div>
				{this.state.loading && <div className='waveform-progress'>
					<div className="progress-bar-buffer" style={{ width: `${this.state.bufferProgress}%` }} >
						{(Math.round(this.state.bufferProgress) > 3) ? `${Math.round(this.state.bufferProgress)}%` : ""}
					</div>
				</div>}
			</div>
		);
	}
}